import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Clock } from "lucide-react";
import { partnerApi } from "@/lib/partner";
import { PartnerShell, PartnerLoading, PartnerEmpty, PartnerError } from "@/components/partner/PartnerShell";

function money(cents, cur) {
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency: (cur || "USD").toUpperCase() })
      .format((Number(cents) || 0) / 100);
  } catch { return `${cents}`; }
}

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

const STATUSES = ["all", "queued", "processing", "paid", "cancelled"];
const STATUS_STYLES = {
  queued: "text-amber-300 border-amber-500/40 bg-amber-500/10",
  processing: "text-sky-300 border-sky-500/40 bg-sky-500/10",
  paid: "text-emerald-300 border-emerald-500/40 bg-emerald-500/10",
  cancelled: "text-white/50 border-white/15 bg-white/[0.03]",
};

// Minimal "Payout details" surface. The dashboard banner links here — we don't
// have a real self-service payout-onboarding flow yet, so render an honest
// "Coming soon" panel with a contact-support fallback rather than shipping a
// fake form that pretends to save.
const PayoutDetails = ({ payoutReady }) => (
  <div
    id="payout-details"
    className="mt-6 rounded-2xl border border-white/10 bg-white/[0.02] p-5"
    data-testid="partner-payout-details"
  >
    <div className="flex flex-wrap items-start justify-between gap-4">
      <div className="min-w-0">
        <h2 className="text-sm font-medium text-white">Payout details</h2>
        <p className="mt-1 text-xs text-white/55">
          Where we send your earnings once you clear the monthly minimum.
        </p>
      </div>
      <span
        className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] ${
          payoutReady
            ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300"
            : "border-amber-500/40 bg-amber-500/10 text-amber-200"
        }`}
        data-testid="partner-payout-status-chip"
      >
        {payoutReady ? "Ready" : "Not set up"}
      </span>
    </div>

    <div className="mt-4 flex items-start gap-3 rounded-xl border border-white/8 bg-white/[0.02] p-4 text-xs text-white/60">
      <Clock className="mt-0.5 h-4 w-4 shrink-0 text-white/45" />
      <div>
        <p className="text-white/85">Self-service setup is coming soon.</p>
        <p className="mt-1">
          Meanwhile, email{" "}
          <a
            href="mailto:partners@tappresence.com?subject=Payout%20details"
            className="text-[#D6A653] underline underline-offset-4"
            data-testid="partner-payout-contact"
          >
            partners@tappresence.com
          </a>{" "}
          with your preferred method (Bank / Wise or PayPal) and we&rsquo;ll get you
          set up before your first payout.
        </p>
      </div>
    </div>
  </div>
);

export default function PartnerPayouts() {
  const [status, setStatus] = useState("all");
  const [rows, setRows] = useState([]);
  const [payoutReady, setPayoutReady] = useState(false);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const params = { limit: 200 };
      if (status !== "all") params.status = status;
      const [r, dash] = await Promise.all([
        partnerApi.payouts(params),
        partnerApi.dashboard().catch(() => null),
      ]);
      setRows(Array.isArray(r?.items) ? r.items : []);
      setPayoutReady(Boolean(dash?.partner?.payout_ready));
    } catch (e) {
      setErr(e?.response?.data?.detail || "load_failed");
    } finally { setLoading(false); }
  }, [status]);
  useEffect(() => { load(); }, [load]);

  const download = (id) => {
    const token = localStorage.getItem("ariadni_token") || localStorage.getItem("token");
    fetch(`${BACKEND}/api/partner/payouts/${id}/statement.csv`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(async (resp) => {
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const blob = await resp.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = `payout_${id}.csv`;
        document.body.appendChild(a); a.click();
        setTimeout(() => { URL.revokeObjectURL(url); a.remove(); }, 500);
      })
      .catch((e) => toast.error(`Download failed: ${e.message}`));
  };

  return (
    <PartnerShell testId="partner-payouts-page">
      <div className="flex items-baseline justify-between gap-3">
        <h1 className="text-3xl font-light tracking-tight">Payouts</h1>
        <p className="text-xs text-white/45">Monthly, once approved commissions clear the minimum threshold.</p>
      </div>

      <PayoutDetails payoutReady={payoutReady} />

      <div className="mt-8 flex flex-wrap items-center gap-2">
        {STATUSES.map((s) => (
          <button
            key={s}
            onClick={() => setStatus(s)}
            data-testid={`payout-filter-${s}`}
            className={`rounded-full border px-3 py-1.5 text-xs capitalize ${status === s
              ? "border-[#D6A653] bg-[#D6A653]/12 text-[#D6A653]"
              : "border-white/12 text-white/55 hover:text-white"}`}
          >
            {s}
          </button>
        ))}
      </div>

      {loading ? (
        <PartnerLoading testId="partner-payouts-loading" />
      ) : err ? (
        <PartnerError
          testId="partner-payouts-error"
          message={err === "not_a_partner" ? "You’re not a partner yet." : "We couldn’t load your payouts."}
          onRetry={err === "not_a_partner" ? undefined : load}
        />
      ) : rows.length === 0 ? (
        <PartnerEmpty
          testId="partner-payouts-empty"
          title="No payouts yet"
          sub="Payouts are issued monthly once your approved commissions exceed $50."
        />
      ) : (
        <div className="mt-6 overflow-x-auto rounded-2xl border border-white/10 bg-[#0B0D12]">
          <table className="min-w-full text-sm">
            <thead className="text-left text-[11px] uppercase tracking-wider text-white/40">
              <tr>
                <th className="px-4 py-3">Period</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">Amount</th>
                <th className="px-4 py-3 text-right">Commissions</th>
                <th className="px-4 py-3">Method</th>
                <th className="px-4 py-3">External ref</th>
                <th className="px-4 py-3">Paid at</th>
                <th className="px-4 py-3">Statement</th>
              </tr>
            </thead>
            <tbody className="text-white/80">
              {rows.map((p) => (
                <tr key={p?.id} className="border-t border-white/5" data-testid={`payout-row-${p?.id}`}>
                  <td className="px-4 py-3 font-mono text-white/70">{p?.planned_for_period || "—"}</td>
                  <td className="px-4 py-3">
                    <span className={`rounded-full border px-2 py-0.5 text-[11px] capitalize ${STATUS_STYLES[p?.status] || ""}`}>{p?.status || "—"}</span>
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums">{money(p?.amount_cents ?? 0, p?.currency)}</td>
                  <td className="px-4 py-3 text-right tabular-nums">{p?.commissions_count ?? 0}</td>
                  <td className="px-4 py-3 text-white/60 capitalize" data-testid={`payout-method-${p?.id}`}>
                    {p?.method === "manual_bank"
                      ? "Bank / Wise"
                      : ((p?.method || "").replace("_", " ") || "—")}
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-white/50">{p?.external_ref || "—"}</td>
                  <td className="px-4 py-3 text-xs text-white/50">{p?.paid_at ? new Date(p.paid_at).toLocaleDateString() : "—"}</td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => download(p?.id)}
                      data-testid={`download-statement-${p?.id}`}
                      className="rounded-lg border border-white/15 px-2.5 py-1 text-xs text-white/70 hover:bg-white/5"
                    >
                      CSV
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </PartnerShell>
  );
}
